/**
 * Pool de concurrencia acotada.
 *
 * Por qué acotar: más conexiones simultáneas NO significa más velocidad. En un
 * enlace doméstico peruano (donde una boda de 400 RAW puede tardar 2 horas),
 * abrir 30 sockets satura el buffer de subida y hace que TODAS las partes vayan
 * lentas, además de multiplicar los timeouts.
 *
 * En móvil el límite es aún más bajo: las radios celulares penalizan la
 * multiplexación y el navegador puede recortar conexiones en segundo plano.
 */
export class ConcurrencyPool {
  private active = 0
  private readonly queue: Array<() => void> = []
  private readonly errors: unknown[] = []

  constructor(private readonly limit: number) {
    if (limit < 1) throw new RangeError('El límite de concurrencia debe ser >= 1')
  }

  /** Encola trabajo. No espera: usa drain() para esperar a todo. */
  run<T>(task: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const start = async () => {
        this.active++
        try {
          resolve(await task())
        } catch (error) {
          this.errors.push(error)
          // Normalizado a Error: una parte que falla puede rechazar con
          // cualquier cosa, y un catch que recibe `undefined` no deja rastro
          // de qué se rompió durante una subida de dos horas.
          reject(error instanceof Error ? error : new Error(String(error)))
        } finally {
          this.active--
          this.queue.shift()?.()
        }
      }

      if (this.active < this.limit) {
        void start()
      } else {
        this.queue.push(() => void start())
      }
    })
  }

  /** Espera a que se vacíe la cola. No lanza: revisa `failures` después. */
  async drain(): Promise<void> {
    while (this.active > 0 || this.queue.length > 0) {
      await new Promise((resolve) => setTimeout(resolve, 50))
    }
  }

  get failures(): readonly unknown[] {
    return this.errors
  }
}

/**
 * Límites según el dispositivo.
 *
 * El desktop de un fotógrafo con fibra aguanta 4 archivos × 4 partes. Un móvil
 * en 4G, no: además de saturar la radio, cada conexión extra consume batería y
 * aumenta la probabilidad de que el sistema mate la pestaña.
 */
export function recommendedConcurrency(): { files: number; parts: number } {
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
  const cores = navigator.hardwareConcurrency ?? 4

  // La API NetworkInformation no está en todos los navegadores; si existe,
  // la usamos para degradar en redes lentas.
  const connection = (navigator as Navigator & { connection?: { effectiveType?: string } }).connection
  const slowNetwork = connection?.effectiveType === '2g' || connection?.effectiveType === 'slow-2g'

  if (slowNetwork) return { files: 1, parts: 1 }
  if (isMobile) return { files: 2, parts: 2 }

  return { files: Math.min(4, Math.max(2, Math.floor(cores / 2))), parts: 4 }
}
